import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Modal, Tag } from "antd";
import Cookies from "js-cookie";
import ProfileViewer from "../borrower-dashboard/ProfileViewer";
import { isCoOwnedProperty } from "../../utils/propertyLoanOwnership";

const API = "https://www.smart-lend.com/api/support/property-applications";
const headers = () => ({
  Authorization: `Bearer ${Cookies.get("support_token")}`,
  Accept: "application/json",
  "Content-Type": "application/json",
});
const tabs = [
  "Personal Information",
  "Corporate Information",
  "NOA History",
  "Capitals",
  "Financials",
  "Shareholders",
  "Borrower Report",
].map((name) => ({ name }));
const statusLabel = {
  pending: "Link ready",
  started: "Scan started",
  resolved: "Awaiting co-owner confirmation",
  completed: "Myinfo saved",
  revoked: "Replaced",
  expired: "Expired",
};
const reviewLabel = {
  pending: "Awaiting support approval",
  approved: "Approved for lenders",
  rejected: "Rejected — hidden from lenders",
};

export default function PropertyCoOwnerInvitations({ application, disabled }) {
  const loan =
    typeof application.loan_data === "string"
      ? JSON.parse(application.loan_data)
      : application.loan_data || {};
  const coOwned = isCoOwnedProperty(loan.propertyOwnership);
  const owners =
    coOwned && Array.isArray(loan.otherOwners) ? loan.otherOwners : [];
  const [invitations, setInvitations] = useState([]);
  const [consents, setConsents] = useState({});
  const [links, setLinks] = useState({});
  const [copiedLink, setCopiedLink] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  const [tab, setTab] = useState(0);
  const active = useRef(true);
  useEffect(() => {
    const resize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  const load = useCallback(async () => {
    if (!coOwned) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/${application.id}/co-owner-myinfo`, {
        headers: headers(),
      });
      const body = await response.json();
      if (!response.ok)
        throw new Error(body.message || "Unable to load Myinfo invitations.");
      if (active.current) {
        setInvitations(body.invitations || []);
        setError("");
      }
    } catch (err) {
      if (active.current) setError(err.message);
    } finally {
      if (active.current) setLoading(false);
    }
  }, [application.id, coOwned]);
  useEffect(() => {
    active.current = true;
    load();
    return () => {
      active.current = false;
    };
  }, [load]);
  const generate = async (index) => {
    const consent = consents[index];
    if (busy != null || reviewing != null || disabled || !consent?.applicant || !consent?.owner)
      return;
    setBusy(index);
    setError("");
    try {
      const response = await fetch(
        `${API}/${application.id}/co-owners/${index}/myinfo-invitations`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({
            applicant_consent: true,
            co_owner_willing: true,
          }),
        },
      );
      const body = await response.json();
      if (!response.ok)
        throw new Error(body.message || "Unable to generate a Myinfo link.");
      if (active.current) {
        setCopiedLink(null);
        setLinks((current) => ({ ...current, [index]: body.url }));
        setConsents((current) => ({ ...current, [index]: {} }));
        await load();
      }
    } catch (err) {
      if (active.current) setError(err.message);
    } finally {
      if (active.current) setBusy(null);
    }
  };
  const review = async (scan, decision) => {
    if (disabled || busy != null || reviewing != null) return;
    setReviewing(`${scan.id}:${decision}`);
    setError("");
    setNotice("");
    try {
      const response = await fetch(`${API}/${application.id}/co-owner-myinfo/${scan.id}/review`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({ decision, expected_review_status: scan.review_status || "pending" }),
      });
      const body = await response.json();
      if (!response.ok) {
        if (response.status === 409) await load();
        throw new Error(body.message || "Unable to review this co-owner scan.");
      }
      await load();
      if (active.current) setNotice(body.message);
    } catch (err) {
      if (active.current) setError(err.message);
    } finally {
      if (active.current) setReviewing(null);
    }
  };
  const consentFor = (index, field, checked) =>
    setConsents((current) => ({
      ...current,
      [index]: { ...current[index], [field]: checked },
    }));
  return (
    <section
      className="support-property-co-owner-myinfo"
      aria-label="Optional co-owner Myinfo"
    >
      <h3>Myinfo profiles</h3>
      <p>
        The main applicant is the Keyman. Co-owner scans are optional; this case
        can be pushed to market with or without them.
      </p>
      {owners.length > 0 && (
        <p>
          Check each co-owner’s Myinfo against the application manually. A
          different name does not prevent the scan from being saved. Lenders
          only see scans you approve. Rejecting a scan hides it from lenders.
        </p>
      )}
      <Button
        onClick={() => {
          setTab(0);
          setProfileOpen(true);
        }}
      >
        View Myinfo profiles
      </Button>
      {owners.length > 0 && (
        <>
          <Button
            style={{ marginLeft: 8 }}
            onClick={load}
            loading={loading}
            disabled={busy != null || reviewing != null}
          >
            Refresh co-owner scans
          </Button>
          {error && <Alert role="alert" type="error" message={error} />}
          {notice && <Alert role="status" type="success" message={notice} />}
          {owners.map((owner, index) => {
            const latest = invitations.find(
              (item) => Number(item.co_owner_index) === index,
            );
            const completed = invitations.find(
              (item) =>
                Number(item.co_owner_index) === index &&
                item.status === "completed",
            );
            const scanned = completed || (latest?.myinfo_name ? latest : null);
            return (
              <section
                className="support-property-co-owner-invitation"
                key={index}
                aria-label={`Co-owner ${index + 1} Myinfo invitation`}
              >
                <h4>{owner.name || `Co-owner ${index + 1}`}</h4>
                <Tag color={completed ? "green" : "default"}>
                  {latest
                    ? statusLabel[latest.status] || latest.status
                    : "No scan requested"}
                </Tag>
                {completed && (
                  <section aria-label={`Co-owner ${index + 1} scan review`}>
                    <p>Myinfo saved on {new Date(completed.completed_at).toLocaleString("en-SG")}.</p>
                    <Tag color={completed.review_status === "approved" ? "green" : completed.review_status === "rejected" ? "red" : "gold"}>
                      {reviewLabel[completed.review_status || "pending"]}
                    </Tag>
                    <p>Submitted scan: <strong>{completed.myinfo_name || completed.name || owner.name}</strong></p>
                    {completed.reviewed_at && <p>Last reviewed on {new Date(completed.reviewed_at).toLocaleString("en-SG")}.</p>}
                    <Button
                      onClick={() => review(completed, "approved")}
                      disabled={disabled || busy != null || reviewing != null || completed.review_status === "approved"}
                      loading={reviewing === `${completed.id}:approved`}
                    >Approve Myinfo</Button>{" "}
                    <Button
                      danger
                      onClick={() => review(completed, "rejected")}
                      disabled={disabled || busy != null || reviewing != null || completed.review_status === "rejected"}
                      loading={reviewing === `${completed.id}:rejected`}
                    >Reject Myinfo</Button>
                  </section>
                )}
                {scanned?.myinfo_name && (
                  <div aria-label={`Co-owner ${index + 1} name comparison`}>
                    <p>
                      Name entered in application: <strong>{scanned.name || owner.name}</strong>
                      <br />
                      Name from Singpass Myinfo: <strong>{scanned.myinfo_name}</strong>
                    </p>
                    {scanned.name_mismatch && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Names differ — check this co-owner manually."
                        description="The scan can still be saved and the case can be pushed to market. Open Myinfo profiles to check the full details."
                      />
                    )}
                  </div>
                )}
                <Checkbox
                  checked={!!consents[index]?.applicant}
                  disabled={disabled || busy != null || reviewing != null}
                  onChange={(event) =>
                    consentFor(index, "applicant", event.target.checked)
                  }
                >
                  Main applicant consent obtained for this co-owner’s Myinfo
                  collection.
                </Checkbox>
                <Checkbox
                  checked={!!consents[index]?.owner}
                  disabled={disabled || busy != null || reviewing != null}
                  onChange={(event) =>
                    consentFor(index, "owner", event.target.checked)
                  }
                >
                  This co-owner is willing to share their Myinfo for this case.
                </Checkbox>
                <Button
                  loading={busy === index}
                  disabled={
                    disabled ||
                    busy != null ||
                    reviewing != null ||
                    !consents[index]?.applicant ||
                    !consents[index]?.owner
                  }
                  onClick={() => generate(index)}
                >
                  {latest ? "Generate new Myinfo link" : "Generate Myinfo link"}
                </Button>
                {links[index] && (
                  <>
                    <label htmlFor={`property-co-owner-link-${index}`}>
                      Share this link with {owner.name}
                    </label>
                    <input
                      id={`property-co-owner-link-${index}`}
                      type="text"
                      value={links[index]}
                      readOnly
                      onFocus={(event) => event.target.select()}
                    />
                    <Button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(links[index]);
                          setCopiedLink(index);
                        } catch {
                          setError(
                            "Select the link above and copy it manually.",
                          );
                        }
                      }}
                    >
                      {copiedLink === index ? "Link copied" : "Copy link"}
                    </Button>{" "}
                    <a
                      href={links[index]}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open invitation link
                    </a>
                    <small>
                      Use Copy link to share the complete invitation. Expires
                      after 7 days. Generating a new link replaces any
                      unfinished invitation.
                    </small>
                  </>
                )}
              </section>
            );
          })}
        </>
      )}
      <Modal
        open={profileOpen}
        onCancel={() => setProfileOpen(false)}
        footer={null}
        width={1200}
        destroyOnClose
        title="Property Myinfo profiles"
      >
        {profileOpen && (
          <ProfileViewer
            isMobile={mobile}
            propertyApplicationId={application.id}
            propertyMyinfoSupportMode
            personalOnly
            viewingUser={0}
            setViewingUser={() => {}}
            viewingTab={tab}
            setViewingTab={setTab}
            setLiveUserData={() => {}}
            availableTab={tabs}
            combinedSingpassData={[]}
            allCBSData={[]}
            otherAuthoriserDetails={[]}
          />
        )}
      </Modal>
    </section>
  );
}
